import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, Fade, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";

// Mock data to demonstrate the AI matching capabilities
const showcaseData = [
  {
    query: "a matching printed dupatta for my red dress",
    gender: "Women",
    products: [
      {
        id: 1,
        title: "Red Printed Dupatta",
        brand: "Diners",
        image_url: require("../../assets/images/Red Printed Dupatta_diners.jpg"),
      },
      {
        id: 2,
        title: "Printed Chiffon Dupatta",
        brand: "Sapphire",
        image_url: require("../../assets/images/Printed Chiffon Dupatta_Sapphire.webp"),
      },
      {
        id: 3,
        title: "Stitched Dupatta",
        brand: "Sana Safinaz",
        image_url: require("../../assets/images/Stitched Dupatta_SanaSafinaz.webp"),
      },
    ],
  },
  {
    query: "embroidered dresses under 10,000",
    gender: "Women",
    products: [
      {
        id: 4,
        title: "Embroidered Dress",
        brand: "ETHNC",
        image_url: require("../../assets/images/embroidered dress_ethnc.webp"),
      },
      {
        id: 5,
        title: "Embroidered Raw Silk Suit",
        brand: "Edenrobe",
        image_url: require("../../assets/images/Embroidered Raw Silk Suit_edenrobe.webp"),
      },
      {
        id: 6,
        title: "Embroidered Slub Stitched",
        brand: "Zeenwomen",
        image_url: require("../../assets/images/EMBROIDERED SLUB STITCHED SHIRT BOTTOM SUIT_zeenwomen.webp"),
      },
    ],
  },
  {
    query: "vacation look for women",
    gender: "Women",
    products: [
      {
        id: 7,
        title: "Indigo Palm Resort Shirt",
        brand: "Lama",
        image_url: require("../../assets/images/INDIGO PALM RESORT SHIRT_Lama.webp"),
      },
      {
        id: 8,
        title: "T-Shirt",
        brand: "ETHNC",
        image_url: require("../../assets/images/Tshirt_Ethnc.webp"),
      },
      {
        id: 9,
        title: "Cropped Printed Shirt",
        brand: "Outfitters",
        image_url: require("../../assets/images/Cropped Printed Shirt_outfitters.webp"),
      },
    ],
  },
  {
    query: "mother’s day gift ideas",
    gender: "Women",
    products: [
      {
        id: 10,
        title: "Hand Bag",
        brand: "Edenrobe",
        image_url: require("../../assets/images/Hand Bag_Edenrobe.webp"),
      },
      {
        id: 11,
        title: "Elegant Silver Tone Watch",
        brand: "Polkadots",
        image_url: require("../../assets/images/Elegant Silver Tone Watch_Polkadots.webp"),
      },
      {
        id: 12,
        title: "Mirage Perfume",
        brand: "Gulahmed Ideas",
        image_url: require("../../assets/images/Mirage Perfume_Gulahmed Ideas.webp"),
      },
    ],
  },
  {
    query: "formal shoes for men to match a blue suit",
    gender: "Men",
    products: [
      {
        id: 13,
        title: "Black Men Formal Shoes",
        brand: "Diners",
        image_url: require("../../assets/images/Black Men Formal Shoes_Diners.jpg"),
      },
      {
        id: 14,
        title: "Blue Formal Shoes",
        brand: "1st Step",
        image_url: require("../../assets/images/Blue Formal Shoes_1ststep.webp"),
      },
      {
        id: 15,
        title: "Leather Suit Shoes",
        brand: "Mango",
        image_url: require("../../assets/images/LEATHER_SUIT_SHOES mango.jpg"),
      },
    ],
  },
];

const DynamicShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [minHeight, setMinHeight] = useState('auto');
  const gridContentRef = useRef(null);

  useEffect(() => {
    setMinHeight('auto');
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (gridContentRef.current) {
      const currentHeight = gridContentRef.current.offsetHeight;
      if (currentHeight > 0) {
        setMinHeight((prev) => {
          if (prev === 'auto') return `${currentHeight}px`;
          const prevVal = parseFloat(prev);
          return `${Math.max(prevVal, currentHeight)}px`;
        });
      }
    }
  }, [currentIndex, isMobile, isTablet]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false); // Start fading out

      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % showcaseData.length);
        setFade(true); // Fade back in with new data
      }, 500); // Wait for half a second for the fade out to finish before changing data

    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const currentItem = showcaseData[currentIndex];

  return (
    <Box
      sx={{
        maxWidth: { xs: "100%", md: "960px" },
        mx: "auto",
        px: isMobile ? 2.5 : 4,
        pt: isMobile ? 4 : 5,
        pb: 1,
      }}
    >
      <Box sx={{ mb: 3, textAlign: "center", minHeight: { xs: "120px", sm: "140px", md: "160px" } }}>
        <Typography
          sx={{
            fontFamily: "Poppins-Medium",
            fontSize: "14px",
            color: "#888",
            textTransform: "uppercase",
            letterSpacing: "2px",
            mb: 2,
          }}
        >
          Handpik in Action
        </Typography>

        <Fade in={fade} timeout={500}>
          <Typography
            onClick={() => navigate(`/search?q=${encodeURIComponent(currentItem.query)}&gender=${encodeURIComponent(currentItem.gender)}`)}
            sx={{
              fontFamily: "'Lora', serif",
              fontSize: isMobile ? "28px" : isTablet ? "36px" : "48px",
              fontWeight: 500,
              color: "#111",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              maxWidth: "800px",
              mx: "auto",
              cursor: "pointer",
              transition: "opacity 0.2s ease",
              "&:hover": {
                opacity: 0.8
              }
            }}
          >
            Ask for{" "}
            <Box component="span" sx={{ fontStyle: "italic", color: "#FF6B00" }}>
              "{currentItem.query}"
            </Box>
          </Typography>
        </Fade>
      </Box>

      {/* Dynamic Results Grid */}
      <Box sx={{ position: "relative", minHeight }}>
        <Fade in={fade} timeout={500}>
          <Grid ref={gridContentRef} container spacing={isMobile ? 2 : 4} justifyContent="center">
            {currentItem.products.map((product) => (
              <Grid item xs={4} sm={4} md={4} key={product.id}>
                <Box
                  onClick={() => navigate(`/search?q=${encodeURIComponent(currentItem.query)}&gender=${encodeURIComponent(currentItem.gender)}`)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    "&:hover .product-image-container": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 32px rgba(0,0,0,0.1)",
                    },
                    "&:hover .product-image": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    className="product-image-container"
                    sx={{
                      width: "100%",
                      aspectRatio: { xs: "2/3", sm: "4/5" },
                      height: { xs: "auto", sm: "280px", md: "350px" },
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "#F9F9F9",
                      transition: "all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      mb: 2.5,
                      position: "relative",
                    }}
                  >
                    <img
                      className="product-image"
                      src={product.image_url}
                      alt={product.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.7s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontFamily: "Poppins-Medium",
                        fontWeight: 600,
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        mb: 0.5,
                      }}
                    >
                      {product.brand}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "12px", sm: "15px" },
                        fontWeight: 500,
                        color: "#111",
                        fontFamily: "Poppins-Medium",
                        display: "-webkit-box",
                        WebkitLineClamp: { xs: 2, sm: 1 },
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {product.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Fade>
      </Box>
    </Box>
  );
};

export default DynamicShowcase;
